import React from 'react';
import { useSelector } from 'react-redux';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Tooltip from '@mui/material/Tooltip';
import { format } from "date-fns";
import CustomTable from '../table/table';
import './sync-contacts-modal.scss';

/**
 * Table presentation component for selecting and inspecting contacts (Customers, Suppliers, Contractors)
 * during synchronization flows.
 *
 * @param {Object} props
 * @param {'supplier'|'contractor'|'customer'|'all'} props.entityType - Contact entity type
 * @param {'customer'|'supplier'|'contractor'} props.activeModalTab - Current active tab in 'all' view
 * @param {Function} props.setActiveModalTab - Tab change handler
 * @param {Array<string|number>} props.selectedIds - Currently selected contact row IDs
 * @param {Array<Object>} props.modalCustomers - Customer contacts list
 * @param {Array<Object>} props.modalSuppliers - Supplier contacts list
 * @param {Array<Object>} props.modalContractors - Contractor contacts list
 * @param {Function} props.isAllSelectedForCurrentTab - Checks if all rows in current tab are selected
 * @param {Function} props.toggleSelectAllForCurrentTab - Select-all toggle handler for current tab
 * @param {Function} props.toggleRowSelection - Single contact row toggle handler
 * @param {Function} props.getActiveContactsForTab - Returns array of contacts for active tab
 * @param {Object} [props.syncLogs] - Current batch sync status logs
 * @param {Object} [props.persistentSyncStatus] - Persisted backend sync status records
 * @param {boolean} [props.syncing] - Whether a sync operation is in progress
 * @returns {React.ReactElement}
 */
export default function SyncContactsTable({
    entityType,
    activeModalTab,
    setActiveModalTab,
    selectedIds,
    modalCustomers,
    modalSuppliers,
    modalContractors,
    isAllSelectedForCurrentTab,
    toggleSelectAllForCurrentTab,
    toggleRowSelection,
    getActiveContactsForTab,
    syncLogs = {},
    persistentSyncStatus = {},
    syncing = false
}) {
    const user = useSelector((state) => state.user.userData);
    const permissions = user?.permissions || {};
    const hasCustomerWrite = Boolean(user?.global_user || permissions?.customer === 2);
    const hasSupplierWrite = Boolean(user?.global_user || permissions?.supplier === 2);
    const hasContractorWrite = Boolean(user?.global_user || permissions?.contractor === 2);
    const modalCustomerColumns = [
        {
            title: (
                <Form.Check
                    type="checkbox"
                    checked={isAllSelectedForCurrentTab()}
                    onChange={() => toggleSelectAllForCurrentTab()}
                    disabled={syncing}
                />
            ),
            field: "select",
            class: "w-50px",
            render: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleRowSelection(row.id)}
                    disabled={syncing}
                />
            )
        },
        { title: "Customer Number", field: "customerNumber", render: (row) => row.customerNumber || '—' },
        { title: "Company Name", field: "businessName", render: (row) => row.businessName || '—' },
        { title: "Address", field: "address", render: (row) => row.address || '—' },
        { title: "Phone Number", field: "phoneNumber", render: (row) => row.phoneNumber || '—' },
        { title: "Contact", field: "contacts", render: (row) => row.contacts || '—' },
        { title: "Email", field: "email", render: (row) => row.email || '—' },
        { title: "ABN", field: "abn", render: (row) => row.abn || '—' },
        {
            title: "Payment Terms",
            field: "paymentTerms",
            render: (row) => (row.paymentTerms != null && row.paymentTerms !== '' ? `${row.paymentTerms} Days` : '—')
        }
    ];

    const modalVendorColumns = [
        {
            title: (
                <Form.Check
                    type="checkbox"
                    checked={isAllSelectedForCurrentTab()}
                    onChange={() => toggleSelectAllForCurrentTab()}
                    disabled={syncing}
                />
            ),
            field: "select",
            class: "w-50px",
            render: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleRowSelection(row.id)}
                    disabled={syncing}
                />
            )
        },
        { title: "Vendor Number", field: "vendorNumber", render: (row) => row.vendorNumber || '—' },
        { title: "Company Name", field: "businessName", render: (row) => row.businessName || '—' },
        { title: "Address", field: "address", render: (row) => row.address || '—' },
        { title: "Phone Number", field: "phoneNumber", render: (row) => row.phoneNumber || '—' },
        { title: "Contact", field: "contacts", render: (row) => row.contacts || '—' },
        { title: "Email", field: "email", render: (row) => row.email || '—' },
        { title: "ABN", field: "abn", render: (row) => row.abn || '—' },
        {
            title: "Payment Terms",
            field: "paymentTerms",
            render: (row) => (row.paymentTerms != null && row.paymentTerms !== '' ? `${row.paymentTerms} Days` : '—')
        }
    ];

    /**
     * Resolves the current push sync status string for a contact row.
     * @param {Object} row - Contact row data
     * @returns {string} Status code ('pending'|'syncing'|'success'|'error'|'')
     */
    const getContactPushStatusValue = (row) => {
        const log = syncLogs[row?.id];
        if (log) {
            return log.status || '';
        }
        const persistent = persistentSyncStatus[row?.id];
        if (persistent?.isSynced || persistent?.syncStatus === 'success') {
            return 'success';
        }
        if (persistent?.syncStatus === 'failed' || persistent?.syncStatus === 'error') {
            return 'error';
        }
        if (persistent?.syncStatus) {
            return persistent.syncStatus;
        }
        return '';
    };

    const pushStatusColumn = {
        title: "Push Status",
        field: "pushStatus",
        class: "text-center align-middle w-120px",
        customSort: (row) => getContactPushStatusValue(row),
        render: (row) => {
            const log = syncLogs[row.id];
            if (log) {
                return (
                    <>
                        {log.status === 'pending' && (
                            <Tooltip
                                title="Queued"
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <HourglassEmptyIcon className="sync-status-icon sync-status-icon--pending" />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'syncing' && (
                            <Tooltip
                                title="Pushing to accounting platform..."
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <Spinner animation="border" size="sm" className="text-primary sync-spinner-sm" />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'success' && (
                            <Tooltip
                                title={
                                    <div className="text-center">
                                        <div>Synced Successfully</div>
                                        <div className="small text-white-50 mt-1">
                                            Last synced: {format(new Date(), 'dd/MM/yyyy, hh:mm a')}
                                        </div>
                                    </div>
                                }
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <CheckCircleIcon className="sync-status-icon sync-status-icon--success" />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'error' && (
                            <Tooltip
                                title={<div className="sync-error-tooltip-content">{log.msg}</div>}
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{
                                    style: { zIndex: 99999 }
                                }}
                            >
                                <span className="d-inline-flex align-items-center sync-error-link text-danger">
                                    <CancelIcon className="sync-status-icon sync-status-icon--error" />
                                    <span className="ms-1 small fw-semibold">View Error</span>
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            }

            const persistent = persistentSyncStatus[row.id];
            if (persistent?.isSynced || persistent?.syncStatus === 'success') {
                return (
                    <Tooltip
                        title={
                            <div className="text-center">
                                <div>Synced Successfully</div>
                                {persistent?.lastSyncedAt && (
                                    <div className="small text-white-50 mt-1">
                                        Last synced: {format(new Date(persistent.lastSyncedAt), 'dd/MM/yyyy, hh:mm a')}
                                    </div>
                                )}
                            </div>
                        }
                        placement="top"
                        arrow
                        followCursor={true}
                        PopperProps={{ style: { zIndex: 99999 } }}
                    >
                        <span>
                            <CheckCircleIcon className="sync-status-icon sync-status-icon--success" />
                        </span>
                    </Tooltip>
                );
            }

            if (persistent?.syncStatus === 'failed') {
                return (
                    <Tooltip
                        title={<div className="sync-error-tooltip-content">{persistent.errorDetails?.message || persistent.errorDetails?.error || 'Sync failed'}</div>}
                        placement="top"
                        arrow
                        followCursor={true}
                        PopperProps={{ style: { zIndex: 99999 } }}
                    >
                        <span className="d-inline-flex align-items-center sync-error-link text-danger">
                            <CancelIcon className="sync-status-icon sync-status-icon--error" />
                            <span className="ms-1 small fw-semibold">Error</span>
                        </span>
                    </Tooltip>
                );
            }

            return <span className="text-muted">—</span>;
        }
    };

    modalCustomerColumns.push(pushStatusColumn);
    modalVendorColumns.push(pushStatusColumn);

    const renderTable = (columns, data, key) => (
        <CustomTable
            key={key}
            columns={columns}
            data={(data || []).map((row) => ({
                ...row,
                pushStatus: getContactPushStatusValue(row)
            }))}
            toolbar={true}
            itemsPerPageCustom={false}
            itemsPerPage={10}
            exportButton={false}
            addButton={false}
            isEdit={false}
            isDelete={false}
            isMore={false}
            additionalActions={[]}
            fileName={`modal_sync_${key}`}
            isLoading={syncing}
        />
    );

    return (
        <div className="contacts-sync-table-container">
            {entityType === 'all' ? (
                <Tabs
                    activeKey={activeModalTab}
                    onSelect={(k) => {
                        if (!syncing) {
                            setActiveModalTab(k);
                        }
                    }}
                    className="mb-3"
                >
                    {hasCustomerWrite && (
                        <Tab eventKey="customer" title={`Customers (${modalCustomers.length})`} data-cy="sync-contacts-tab-customer">
                            {renderTable(modalCustomerColumns, modalCustomers, 'customer')}
                        </Tab>
                    )}
                    {hasSupplierWrite && (
                        <Tab eventKey="supplier" title={`Suppliers (${modalSuppliers.length})`} data-cy="sync-contacts-tab-supplier">
                            {renderTable(modalVendorColumns, modalSuppliers, 'supplier')}
                        </Tab>
                    )}
                    {hasContractorWrite && (
                        <Tab eventKey="contractor" title={`Contractors (${modalContractors.length})`} data-cy="sync-contacts-tab-contractor">
                            {renderTable(modalVendorColumns, modalContractors, 'contractor')}
                        </Tab>
                    )}
                </Tabs>
            ) : (
                renderTable(
                    entityType === 'customer' ? modalCustomerColumns : modalVendorColumns,
                    getActiveContactsForTab(),
                    entityType
                )
            )}
        </div>
    );
}
